import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_ITEMS, LINKS, scrollToSection } from "@/data/content";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="header-nav-bar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "bg-[#0B1120]/85 backdrop-blur-md border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a
          href="#topo"
          onClick={(e) => go(e, "#topo")}
          className="flex items-center gap-3 group"
          data-testid="header-logo-link"
        >
          <Logo size={28} />
          <span className="leading-none">
            <span className="block font-display font-extrabold tracking-tight text-white text-sm sm:text-base group-hover:text-[#00E5A3] transition-colors duration-300">
              JOÃO GARCEZ
            </span>
            <span className="block font-mono text-[9px] sm:text-[10px] tracking-[0.35em] text-white/60 mt-1">
              TRAVEL DESIGNER
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Navegação principal">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={item.testid}
              onClick={(e) => go(e, item.href)}
              className="relative text-sm font-medium text-white/75 hover:text-white transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#FF5A36] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={LINKS.cotacao}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#FF5A36] hover:bg-[#00E5A3] hover:text-[#0B1120] text-white text-sm font-semibold px-5 py-2.5 transition-colors duration-300"
          >
            <MessageCircle size={16} strokeWidth={2.4} />
            WhatsApp
          </a>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#0B1120]/95 backdrop-blur-md border-b border-white/10"
            data-testid="mobile-menu-panel"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV_ITEMS.map((item, i) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  data-testid={`mobile-${item.testid}`}
                  onClick={(e) => go(e, item.href)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="text-lg font-display font-semibold text-white/85 hover:text-[#00E5A3] transition-colors"
                >
                  {item.label}
                </motion.a>
              ))}
              <a
                href={LINKS.cotacao}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-btn"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5A36] text-white font-semibold px-5 py-3"
              >
                <MessageCircle size={18} />
                Fale comigo no WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
